import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  CheckCircle2,
  ShieldCheck,
  Sparkles,
  BadgeCheck,
  Lock,
  Receipt,
  Globe,
  CreditCard,
  Zap,
  FileText,
  LineChart,
  PenLine,
  Loader2,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import {
  fadeUp,
  displaySection,
  kicker,
  sectionWrap,
  sectionInner,
} from './shared';
import {
  detectCurrency,
  fetchServerCurrency,
  cachedGeoCountry,
  openOneTimeCheckout,
} from '../../lib/billing';
import { GuestCheckoutDialog } from '../billing/GuestCheckoutDialog';
import { useAuth } from '../../lib/auth-context';

// Feb-2026 pivot: homepage shows ONE plan — Full Profile Makeover
// (INR 999 / USD 49) — with a Razorpay "Buy Now" CTA that
// auto-provisions the user account and emails a magic-link login on
// successful payment. The Pricing page (/pricing) shows both this plan
// and the high-ticket Executive Leadership Quarterly plan.

const FEATURE_ICONS = [FileText, PenLine, LineChart, Sparkles, FileText];
const FALLBACK_FEATURES = [
  'Complete profile audit — analyse your uploaded PDF up to 3 times',
  'Rewritten Headline, About & Experience copy tuned to your voice',
  'Create up to 10 thought-leadership LinkedIn posts (all types combined)',
  'Premium Resume Makeover (Includes Design & Download)',
  'Peer benchmark deep-dive + branded PDF & PPTX reports',
];

export const PricingSection = () => {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  // Currency default: browser heuristic first (fast paint), then server IP
  // upgrades it (see the useEffect below). Landing section uses upper-case
  // 'INR' / 'USD'.
  const [currency, setCurrency] = useState(() => detectCurrency());
  const [country, setCountry] = useState(cachedGeoCountry());
  const [features, setFeatures] = useState(FALLBACK_FEATURES);
  const [priceInr, setPriceInr] = useState(999);
  const [priceUsd, setPriceUsd] = useState(49);
  const [guestCheckoutOpen, setGuestCheckoutOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Hydrate features / prices from the live catalog. Falls back to the
    // static config above when the endpoint is unreachable so the CTA is
    // never blank.
    import('axios').then(({ default: axios }) => {
      const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
      axios
        .get(`${BACKEND}/api/billing/plans`, { timeout: 8000 })
        .then((res) => {
          if (cancelled) return;
          const plans = res?.data?.plans;
          if (!Array.isArray(plans)) return;
          const p = plans.find((x) => x?.key === 'one_time');
          if (!p) return;
          if (Array.isArray(p.features) && p.features.length) {
            setFeatures(p.features.slice(0, 5));
          }
          if (typeof p.price_inr === 'number') setPriceInr(p.price_inr);
          if (typeof p.price_usd === 'number') setPriceUsd(p.price_usd);
        })
        .catch(() => {});
    });
    // Authoritative geoip: hides the "other" tab when the caller is
    // resolved to a specific country.
    fetchServerCurrency().then((geo) => {
      if (cancelled || !geo) return;
      setCountry(geo.country || '');
      if (geo.currency) setCurrency(geo.currency);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const isInr = currency === 'INR';
  const priceLabel = isInr
    ? `\u20b9${priceInr.toLocaleString('en-IN')}`
    : `$${priceUsd}`;

  const handleBuy = async () => {
    if (busy) return;
    if (!user) {
      // Guest checkout dialog collects email/name/phone then opens the
      // Razorpay SDK. Webhook auto-provisions the account on success.
      setGuestCheckoutOpen(true);
      return;
    }
    setBusy(true);
    try {
      await openOneTimeCheckout({
        ctaLabel: 'homepage_buy_now',
        onSuccess: (result) => {
          const cur = String(result?.currency || currency || 'INR').toUpperCase();
          const region = cur === 'INR' ? 'in' : 'us';
          const amt = result?.amount_paise
            ? Number(result.amount_paise) / 100
            : undefined;
          const params = new URLSearchParams();
          if (result?.plan) params.set('plan', result.plan);
          if (cur) params.set('currency', cur);
          if (amt != null) params.set('amount', String(amt));
          if (result?.order_id) params.set('order_id', result.order_id);
          if (result?.payment_id) params.set('payment_id', result.payment_id);
          navigate(`/thank-you/${region}?${params.toString()}`);
        },
        onDismiss: () => setBusy(false),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={sectionWrap} aria-label="Pricing" id="pricing">
      <div className={sectionInner}>
        <motion.div {...fadeUp} className="mb-10 max-w-3xl">
          <p className={kicker}>Pricing</p>
          <h2 className={`${displaySection} mt-4 text-4xl sm:text-5xl lg:text-6xl`}>
            One plan.
            <br />
            <span className="text-primary">A profile that gets meetings.</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
            Ship a fully rewritten, benchmark-tested LinkedIn profile in a single
            evening. No subscription. No auto-renew. Log in the minute you pay.
          </p>
        </motion.div>

        {/* --- The one plan ---------------------------------------------- */}
        <motion.div
          {...fadeUp}
          className="mx-auto max-w-3xl"
          data-testid="pricing-section"
        >
          <Card
            className="relative rounded-3xl border border-primary/40 bg-card shadow-[0_40px_120px_-60px_hsl(220_40%_2%/0.45)]"
            data-testid="pricing-tier-one_time"
          >
            <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full text-[10px] tracking-[0.22em] font-mono uppercase px-3 py-1 whitespace-nowrap z-10">
              <Sparkles size={11} className="mr-1" aria-hidden="true" /> Full Profile Makeover
            </Badge>

            <CardContent className="relative p-7 sm:p-10 flex flex-col">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5">
                <div className="min-w-0">
                  <p
                    className="text-[11px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground"
                    data-testid="pricing-plan-kicker"
                  >
                    One-time payment · 15-day access
                  </p>
                  <h3 className="mt-2 text-2xl sm:text-3xl font-semibold tracking-[-0.02em]">
                    Full Profile Makeover
                  </h3>
                  <p className="mt-2 text-sm sm:text-base text-muted-foreground max-w-md">
                    Everything you need to go from generic to unmistakable — in one sitting.
                  </p>
                </div>
                <div className="shrink-0 text-left sm:text-right">
                  <div className="flex items-end gap-2 sm:justify-end">
                    <span
                      className="font-display font-semibold text-5xl sm:text-6xl tracking-[-0.05em] tabular-nums"
                      data-testid="pricing-plan-price"
                    >
                      {priceLabel}
                    </span>
                    <span className="pb-2 text-sm text-muted-foreground">one-time</span>
                  </div>
                  {isInr ? (
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      Approx. ${priceUsd} · GST invoice available
                    </p>
                  ) : (
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      Charged in USD via Razorpay International
                    </p>
                  )}
                </div>
              </div>

              <Separator className="my-7" />

              <ul
                className="grid gap-4 sm:grid-cols-2"
                data-testid="pricing-plan-features"
              >
                {features.map((f, i) => {
                  const Icon = FEATURE_ICONS[i % FEATURE_ICONS.length];
                  return (
                    <li
                      key={f}
                      className="flex items-start gap-3 rounded-2xl border border-border/60 bg-background/50 p-4"
                      data-testid={`pricing-plan-feature-${i}`}
                    >
                      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                        <Icon size={16} strokeWidth={2.2} aria-hidden="true" />
                      </span>
                      <span className="text-[13px] leading-relaxed">{f}</span>
                    </li>
                  );
                })}
              </ul>

              <div className="mt-8 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-center">
                <div className="flex flex-wrap items-center gap-3 text-[12px] text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5">
                    <ShieldCheck size={13} className="text-primary/80" aria-hidden="true" />
                    Razorpay secure
                  </span>
                  <span aria-hidden="true">·</span>
                  <span className="inline-flex items-center gap-1.5">
                    <Zap size={13} className="text-primary/80" aria-hidden="true" />
                    Login link mailed within seconds
                  </span>
                  <span aria-hidden="true">·</span>
                  <span className="inline-flex items-center gap-1.5">
                    <BadgeCheck size={13} className="text-primary/80" aria-hidden="true" />
                    No auto-renew
                  </span>
                </div>
                <Button
                  onClick={handleBuy}
                  disabled={busy}
                  className="h-12 min-w-[200px] rounded-full text-[14px]"
                  data-testid="pricing-plan-buy-now-btn"
                >
                  {busy ? (
                    <>
                      <Loader2 size={15} className="mr-2 animate-spin" aria-hidden="true" />
                      Opening…
                    </>
                  ) : (
                    <>
                      Buy Now — {priceLabel}
                      <ArrowRight size={15} className="ml-2" aria-hidden="true" />
                    </>
                  )}
                </Button>
              </div>

              <p className="mt-4 text-center text-[11px] text-muted-foreground">
                Looking for a quarterly, fully-managed engagement?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/pricing')}
                  className="underline underline-offset-4 hover:text-foreground"
                  data-testid="pricing-see-quarterly-link"
                >
                  See the Executive Leadership Quarterly plan
                </button>
                .
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Trust pillars below the plan */}
        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.1 }}
          className="mt-10"
          data-testid="pricing-trust-pillars"
        >
          <Card className="rounded-3xl border border-border/70 bg-card/70 backdrop-blur">
            <CardContent className="p-6 sm:p-8">
              <div className="mb-6">
                <p className="text-[11px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground">
                  Why it&rsquo;s safe to start
                </p>
                <h3 className="mt-1.5 font-display font-semibold text-2xl sm:text-3xl tracking-[-0.03em]">
                  Zero-risk to try. Built on standards you already trust.
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                <TrustPillar
                  Icon={ShieldCheck}
                  iconBg="bg-[hsl(var(--ep-accent-soft,212_100%_96%))]"
                  iconColor="text-primary"
                  title="Razorpay secure"
                  desc="PCI-DSS Level 1 gateway used by 10M+ Indian businesses."
                  testid="trust-pillar-razorpay"
                />
                <TrustPillar
                  Icon={BadgeCheck}
                  iconBg="bg-sky-100 dark:bg-sky-500/15"
                  iconColor="text-primary"
                  title="No auto-renew"
                  desc="One-time charge, one-time only. Nothing hits your card again."
                  testid="trust-pillar-cancel"
                />
                <TrustPillar
                  Icon={Lock}
                  iconBg="bg-secondary"
                  iconColor="text-foreground"
                  title="256-bit encryption"
                  desc="Your card never touches our servers. End-to-end SSL on every page."
                  testid="trust-pillar-encryption"
                />
                <TrustPillar
                  Icon={Receipt}
                  iconBg="bg-amber-100 dark:bg-amber-500/15"
                  iconColor="text-foreground"
                  title="No hidden fees"
                  desc="The price you see is the price you pay. GST invoice on request."
                  testid="trust-pillar-no-hidden-fees"
                />
                <TrustPillar
                  Icon={Globe}
                  iconBg="bg-secondary"
                  iconColor="text-foreground"
                  title="Global cards welcome"
                  desc="Visa, Mastercard, Amex, UPI, Net banking, and most international cards."
                  testid="trust-pillar-international"
                />
                <TrustPillar
                  Icon={CreditCard}
                  iconBg="bg-emerald-100 dark:bg-emerald-500/15"
                  iconColor="text-emerald-700 dark:text-emerald-400"
                  title="Instant access"
                  desc="Pay and a one-click login link lands in your inbox within seconds."
                  testid="trust-pillar-instant"
                />
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      <GuestCheckoutDialog
        open={guestCheckoutOpen}
        onOpenChange={setGuestCheckoutOpen}
        planLabel="Full Profile Makeover"
        priceLabel={priceLabel}
      />
    </section>
  );
};

const TrustPillar = ({ Icon, iconBg, iconColor, title, desc, testid }) => (
  <div
    className="rounded-2xl border border-border/70 bg-background/70 backdrop-blur p-4 hover:-translate-y-0.5 transition-transform duration-200"
    data-testid={testid}
  >
    <div className={`inline-flex h-9 w-9 items-center justify-center rounded-lg ${iconBg} ${iconColor}`}>
      <Icon size={18} strokeWidth={2.2} aria-hidden="true" />
    </div>
    <p className="font-semibold mt-3 text-sm tracking-[-0.01em]">{title}</p>
    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{desc}</p>
  </div>
);
